import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { isBusiness } from '@/lib/roles';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AuthCallback() {
  const hasProcessed = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { completeAuth, checkAuth } = useAuth();
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = location.hash || window.location.hash;
    const params = new URLSearchParams(hash.substring(1));
    const sessionId = params.get('session_id');
    
    if (!sessionId) {
      tryFallbackAuth();
      return;
    }

    exchangeSession(sessionId);
  }, []);

  const exchangeSession = async (sessionId) => {
    const loginType = sessionStorage.getItem('womio_login_type') || 'user';
    sessionStorage.removeItem('womio_login_type');

    // Retry up to 2 times
    for (let attempt = 1; attempt <= 2; attempt++) {
      try {
        const res = await axios.post(`${API}/auth/session`, { 
          session_id: sessionId, 
          login_type: loginType 
        }, { withCredentials: true });
        
        completeAuth(res.data);
        redirectUser(res.data);
        return;
      } catch (err) {
        console.error(`Session exchange attempt ${attempt} failed:`, err?.response?.status);
        if (attempt < 2) {
          await new Promise(r => setTimeout(r, 500));
        }
      }
    }

    // Session exchange failed after retries — try fallback /me
    await tryFallbackAuth();
  };

  const tryFallbackAuth = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
      if (res.data && res.data.user_id) {
        completeAuth(res.data);
        redirectUser(res.data);
        return;
      }
    } catch (err) {
      console.error('Fallback /auth/me failed', err);
    }
    setError('Login non riuscito. Riprova.');
    setTimeout(() => navigate('/', { replace: true }), 2000);
  };

  const redirectUser = (userData) => {
    const role = userData?.role;
    let redirectTo = '/dashboard';
    if (isBusiness(userData)) redirectTo = '/restaurant/dashboard';
    
    // Navigate after ensuring state is committed
    setTimeout(() => {
      navigate(redirectTo, { replace: true });
    }, 200);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        {error ? (
          <div>
            <p className="text-destructive font-sans text-sm mb-2">{error}</p>
            <p className="text-muted-foreground font-sans text-xs">Reindirizzamento...</p>
          </div>
        ) : (
          <>
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground font-sans">Accesso in corso...</p>
          </>
        )}
      </div>
    </div>
  );
}
